import React, { useState } from 'react';
import ProductLineBreakdown from './PLbreakdown';

// --- Inline SVG Icons (Replacing Lucide) ---

const IconExternalLink = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
);

const IconActivity = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
);

const IconTarget = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>
);

const IconChevronDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
);

const IconPointer = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 14a8 8 0 0 1-8 8" /><path d="M18 11c.67 0 1.33.33 1.33 1s-.67 1-1.33 1H5.4l-.9-4.43a.5.5 0 0 1 .8-.57l2.1 1.7V5.33a1.33 1.33 0 0 1 2.67 0V8h1.33V4.33a1.33 1.33 0 0 1 2.67 0V8h1.33V5.33a1.33 1.33 0 0 1 2.67 0V11Z" /></svg>
);

const IconLayers = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>
);

const IconBarChart = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></svg>
);

// --- Shared Components ---

const Card = ({ title, icon: Icon, children, buttonText }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 overflow-hidden mb-4 transition-all duration-300 hover:shadow-md flex flex-col h-full">
        <div className="flex justify-between items-center px-5 py-3.5 border-b border-slate-100 bg-white">
            <div className="flex items-center space-x-3">
                <div className="p-1.5 bg-[#f0f6fa] rounded-lg text-[#245c81]">
                    {Icon && <Icon />}
                </div>
                <h2 className="text-[17px] font-bold text-slate-800 tracking-tight">{title}</h2>
            </div>
            {buttonText && (
                <button className="flex items-center space-x-1.5 bg-[#234b63] hover:bg-[#1a384a] text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow">
                    <span>{buttonText}</span>
                    <IconExternalLink />
                </button>
            )}
        </div>
        <div className="p-0 overflow-x-auto custom-scrollbar flex-1">
            {children}
        </div>
    </div>
);

// --- Multiple Funnel Charts Component ---

const FunnelChartDiagram = ({ data, title, onC2Click }) => {
    const isClickableFunnel = title === "MTD" || title === "C/o MTD";

    const layers = [
        { name: "C0", color: "bg-[#4581a3]", width: "w-6/6" },
        { name: "C1", color: "bg-[#619abb]", width: "w-5/6" },
        { name: "C1A", color: "bg-[#7eb6d5]", width: "w-4/6" },
        {
            name: "C2",
            color: "bg-[#a3d2ea]",
            width: "w-3/6",
            // clickable: isClickableFunnel,
        },
        { name: "Retail", color: "bg-[#cce8f7]", width: "w-[43%]" },
    ];

    if (!data) return <div className="p-4 text-slate-400 text-xs italic">Data not available</div>;

    return (
        <div className="flex flex-col  items-center relative">
            <header className="text-center w-full bg-[#1e4b67] text-white py-2 rounded-t-xl text-sm font-bold mb-1 shadow-sm">
                {title}
            </header>

            <div className="relative w-52 pb-4">
                {layers.map((layer, index) => {
                    const isDarkText = layer.name === "Retail" || layer.name === "C2" || layer.name === "C1A";
                    return (
                        <div
                            key={index}
                            className={`${layer.width} h-[28px] ${layer.color} mx-auto mt-1.5 rounded-full flex items-center justify-center ${isDarkText ? 'text-[#1e3a4c]' : 'text-white'} text-[11px] font-bold whitespace-nowrap shadow-sm border border-white/10
                ${layer.clickable ? "cursor-pointer hover:brightness-95 active:scale-95 transition-all" : "cursor-default"}`}
                            title={`${layer.name}: ${data[layer.name]}`}
                            onClick={() => {
                                if (layer.clickable && onC2Click) {
                                    onC2Click(title, layer.name);
                                }
                            }}
                        >
                            <div className="flex items-center px-2">
                                {layer.name}: {data[layer.name]}
                                {layer.clickable && layer.name === "C2" && (
                                    <span className="ml-1.5">
                                        <IconPointer />
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const MultipleFunnelCharts = ({ data }) => {
    const Powerdata = {
        mtd: { C0: data?.mtd?.mtd_c0, C1: data?.mtd?.mtd_c1, C1A: data?.mtd?.mtd_c1A, C2: data?.mtd?.mtd_c2, Retail: data?.mtd?.mtd_retail },
        lmtd: { C0: data?.lmtd?.lmtd_c0, C1: data?.lmtd?.lmtd_c1, C1A: data?.lmtd?.lmtd_c1a, C2: data?.lmtd?.lmtd_c2, Retail: data?.lmtd?.lmtd_retail },
        carryOver: { C0: data?.carryover?.carryover_c0, C1: data?.carryover?.carryover_c1, C1A: data?.carryover?.carryover_c1a, C2: data?.carryover?.carryover_c2, Retail: data?.carryover?.carryover_retail },
        total: { C0: data?.total?.total_c0, C1: data?.total?.total_c1, C1A: data?.total?.total_c1a, C2: data?.total?.total_c2, Retail: data?.total?.total_retail }
    };

    const funnelItems = [
        { title: "MTD", data: Powerdata.mtd },
        { title: "LMTD", data: Powerdata.lmtd },
        { title: "C/o MTD", data: Powerdata.carryOver },
        { title: "Total", data: Powerdata.total },
    ];

    const handleC2Click = (funnelType, layerName) => {
        // console.log(`Action: Open details for ${layerName} in ${funnelType}`);
    };

    return (
        <div className=" bg-white w-[350px] md:w-[1200px] p-2">
            <div className="grid grid-cols-1  sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {funnelItems.map((item, index) => (
                    <FunnelChartDiagram
                        key={index}
                        data={item.data}
                        title={item.title}
                        onC2Click={handleC2Click}
                    />
                ))}
            </div>
        </div>
    );
};

// --- Data Tables ---

const ThGroup = ({ children, colSpan, rowSpan, className = "" }) => (
    <th colSpan={colSpan} rowSpan={rowSpan} className={`bg-[#234b63] text-white font-semibold px-2 py-1.5 border border-[#315d7a] text-center text-sm sticky top-0 z-30 shadow-sm ${className}`}>
        {children}
    </th>
);

const ThSub = ({ children, className = "" }) => (
    <th className={`bg-[#2d5c7a] text-[#e0f2fe] font-medium px-1.5 py-1.5 border border-[#386c8f] text-center text-[13px] leading-tight whitespace-nowrap sticky top-[33px] z-30 shadow-sm ${className}`}>
        {children}
    </th>
);

const TdRowHeader = ({ children, className = "", isTotal = false }) => (
    <td className={`px-2 py-1.5 border border-slate-200 text-left text-sm whitespace-nowrap ${isTotal ? 'bg-[#d8eaf5] text-[#1e3a4c] font-bold' : 'bg-slate-50/80 text-slate-800 font-semibold'} ${className}`}>
        {children}
    </td>
);

const Td = ({ children, className = "", isNegative, isTotal = false }) => (
    <td className={`px-2 py-1.5 border border-slate-200 text-center text-sm whitespace-nowrap transition-colors ${isNegative ? 'text-red-600 font-semibold bg-red-50/30' : 'text-slate-600'} ${isTotal ? 'font-bold text-[#1e3a4c]' : ''} ${className}`}>
        {children}
    </td>
);

const RetailOfftakeTable = ({ rows }) => (
    <div className="overflow-x-auto overflow-y-auto max-h-[350px] custom-scrollbar border border-slate-200 shadow-sm mb-2 rounded-lg relative bg-white">
        <table
            id="retailOfftakePerformanceTable"
            className="w-full min-w-max border-collapse text-xs"
        >            <thead>
                <tr>
                    <ThGroup rowSpan={3}>Area</ThGroup>
                    <ThGroup colSpan={11}>Retail</ThGroup>
                    <ThGroup colSpan={4}>Offtake</ThGroup>
                    <ThGroup rowSpan={3}>MTD<br />Stock</ThGroup>
                </tr>
                <tr>
                    <ThGroup rowSpan={2} className="top-[33px]">Opening Stock</ThGroup>
                    <ThGroup rowSpan={2} className="top-[33px]">Month Tgt</ThGroup>
                    <ThGroup rowSpan={2} className="top-[33px]">Ret MTD Tgt</ThGroup>
                    <ThGroup colSpan={5} className="top-[33px]">Actual</ThGroup>
                    <ThGroup rowSpan={2} className="top-[33px]">MTD Ach.</ThGroup>
                    <ThGroup colSpan={2} className="top-[33px]">% Var</ThGroup>
                    <ThGroup colSpan={3} className="top-[33px]">Actual</ThGroup>
                    <ThGroup colSpan={1} className="top-[33px]">% Var</ThGroup>
                </tr>
                <tr>
                    <ThSub className="top-[66px]">Retail MTD</ThSub>
                    <ThSub className="top-[66px]">Retail LMTD</ThSub>
                    <ThSub className="top-[66px]">LYSM MTD</ThSub>
                    <ThSub className="top-[66px]">Total LM</ThSub>
                    <ThSub className="top-[66px]">Total LYSM</ThSub>
                    <ThSub className="top-[66px]">Ret LMTD</ThSub>
                    <ThSub className="top-[66px]">Ret MTD Oct</ThSub>
                    <ThSub className="top-[66px]">Off MTD</ThSub>
                    <ThSub className="top-[66px]">Off LMTD</ThSub>
                    <ThSub className="top-[66px]">Total LM</ThSub>
                    <ThSub className="top-[66px]">Off LMTD</ThSub>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => {
                    const isTotal = row.name === "Grand Total";
                    return (
                        <tr key={i} className={isTotal ? "bg-[#e3eff7] text-[#1e3a4c]" : "hover:bg-slate-50 bg-white"}>
                            <TdRowHeader isTotal={isTotal}>{row.name}</TdRowHeader>
                            <Td isTotal={isTotal}>{row.opStock}</Td>
                            <Td isTotal={isTotal}>{row.retTgt}</Td>
                            <Td isTotal={isTotal}>{row.retMtdTgt}</Td>
                            <Td isTotal={isTotal}>{row.retMtd}</Td>
                            <Td isTotal={isTotal}>{row.retLmtd}</Td>
                            <Td isTotal={isTotal}>{row.retLysm}</Td>
                            <Td isTotal={isTotal}>{row.retTotLm}</Td>
                            <Td isTotal={isTotal}>{row.retTotLysm}</Td>
                            <Td isTotal={isTotal}>{row.retMtdAch}</Td>
                            <Td isTotal={isTotal} isNegative={row.retVarLmtd?.startsWith('-')}>{row.retVarLmtd}</Td>
                            <Td isTotal={isTotal} isNegative={row.retVarOct?.startsWith('-')}>{row.retVarOct}</Td>
                            <Td isTotal={isTotal}>{row.offMtd}</Td>
                            <Td isTotal={isTotal}>{row.offLmtd}</Td>
                            <Td isTotal={isTotal}>{row.offTotLm}</Td>
                            <Td isTotal={isTotal} isNegative={row.offVarLmtd?.startsWith('-')}>{row.offVarLmtd}</Td>
                            <Td isTotal={isTotal}>{row.mtdStock}</Td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const ZonePerformanceSection = () => {
    const productRows = [
        { name: "HCV", opStock: 450, retTgt: 100, retMtdTgt: 90, retMtd: 82, retLmtd: 75, retLysm: 68, retTotLm: 245, retTotLysm: 230, retMtdAch: "91%", retVarLmtd: "9.3%", retVarOct: "20.5%", mtdStock: 440 },
        { name: "ILMCV", opStock: 320, retTgt: 150, retMtdTgt: 135, retMtd: 128, retLmtd: 140, retLysm: 130, retTotLm: 410, retTotLysm: 395, retMtdAch: "94%", retVarLmtd: "-8.5%", retVarOct: "-1.5%", mtdStock: 310 },
        { name: "Grand Total", opStock: 2520, retTgt: 1180, retMtdTgt: 1062, retMtd: 1065, retLmtd: 1090, retLysm: 1008, retTotLm: 3300, retTotLysm: 3105, retMtdAch: "100%", retVarLmtd: "-2.2%", retVarOct: "5.6%", mtdStock: 2450 }
    ];
    return (
        <Card title="Zone Performance - Retail & Offtake" icon={IconTarget}>
            <div className="p-4 bg-white">
                <RetailOfftakeTable rows={productRows} />
            </div>
        </Card>
    );
};

const TopFilter = () => (
    <div className="w-full border border-slate-200/80 rounded-xl flex flex-wrap items-end p-4 gap-4 bg-white shadow-sm mb-2">
        <div className="flex flex-col gap-1.5 w-full sm:w-56">
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider pl-1">Region/Area</label>
            <div className="border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/50 flex justify-between items-center w-full cursor-pointer hover:border-[#245c81] transition-all">
                <span className="text-[13px] text-slate-800 font-medium">All Regions</span>
                <IconChevronDown />
            </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
            <button className="bg-[#245c81] text-white px-5 py-2 rounded-lg font-medium text-[13px]">Apply</button>
            <button className="bg-white text-slate-600 px-5 py-2 rounded-lg font-medium text-[13px] border border-slate-200">Reset</button>
        </div>
    </div>
);
const TqpPerformanceSection = ({ data, tabledata }) => {


    return (

        <div className="bg-[#f8fafc] p-3">


            <Card
                title="Pipeline Summary"
                icon={IconActivity}
            >
                <div id="tqpFunnelSection">
                    <MultipleFunnelCharts data={data} />
                </div>

            </Card>

            <Card
                title="Pipeline Summary- LOB Breakdown"
                icon={IconLayers}
            >
                <ProductLineBreakdown
                    tabledata={tabledata}
                />

            </Card>

        </div>

    );

};

export default TqpPerformanceSection;